import { useEffect, useState } from 'react';
import { Plus, Trash2, Loader2, MapPin } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import api from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

export default function AdminLocations() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const load = () => api.get('/locations').then(r => setItems(r.data || [])).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e && e.preventDefault();
    const n = name.trim();
    if (!n) return;
    setSaving(true);
    try {
      await api.post('/admin/locations', { name: n });
      toast({ title: 'Location added', description: n });
      setName('');
      load();
    } catch (err) {
      toast({ title: 'Could not add', description: err.response?.data?.detail || 'Please try again.', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const remove = async (loc) => {
    if (!window.confirm(`Delete "${loc.name}"?\n\nExisting listings keep their current location text — they won't be changed.`)) return;
    try {
      await api.delete('/admin/locations/' + loc.id);
      toast({ title: 'Deleted', description: loc.name });
      load();
    } catch {
      toast({ title: 'Delete failed', variant: 'destructive' });
    }
  };

  return (
    <div className="max-w-3xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-neutral-900">Locations</h1>
        <p className="text-sm text-neutral-500">Cities &amp; provinces shown in the property <span className="font-medium">Location</span> dropdown and in public search filters.</p>
      </div>

      <form onSubmit={add} className="bg-white p-5 rounded-xl border border-neutral-200 mb-6 flex gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Add a city or province (e.g. Cavite)" className="h-11" />
        <Button type="submit" disabled={saving} className="btn-primary gap-2 h-11 shrink-0">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add
        </Button>
      </form>

      <div className="bg-white rounded-xl border border-neutral-200 divide-y divide-neutral-100">
        {loading ? (
          <div className="p-6 text-center text-neutral-500">Loading...</div>
        ) : items.length === 0 ? (
          <div className="p-6 text-center text-neutral-500">No locations yet. Add one above.</div>
        ) : items.map((loc) => (
          <div key={loc.id} className="flex items-center justify-between px-5 py-3">
            <span className="flex items-center gap-2 text-neutral-800 font-medium">
              <MapPin className="w-4 h-4 text-[#DC2626]" /> {loc.name}
            </span>
            <Button size="sm" variant="outline" onClick={() => remove(loc)} className="gap-1 text-red-600 border-red-200">
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </Button>
          </div>
        ))}
      </div>

      {!loading && items.length > 0 && (
        <p className="text-xs text-neutral-400 mt-3">{items.length} location{items.length === 1 ? '' : 's'}</p>
      )}
    </div>
  );
}
